import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/apiService';
import { dynastyTheme } from '../services/colorService'; // Import dynastyTheme for consistency if needed elsewhere in context

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status when app loads
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    console.log('🔍 AuthContext: checkAuthStatus initiated.'); // Added log
    try {
      setLoading(true);
      const response = await authAPI.checkAuth();
      if (response.authenticated) {
        setUser(response.user);
        setIsAuthenticated(true);
        console.log('✅ AuthContext: checkAuthStatus completed. Authenticated: true, User:', response.user); // Added log
      } else {
        setUser(null);
        setIsAuthenticated(false);
        console.log('❌ AuthContext: checkAuthStatus completed. Authenticated: false.'); // Added log
      }
    } catch (error) {
      console.error('💥 AuthContext: Auth check failed in checkAuthStatus:', error);
      // Only logout on explicit 401 — not transient network errors
      if (error?.response?.status === 401) {
        setUser(null);
        setIsAuthenticated(false);
      }
      // All other errors (network, timeout, 500): keep current auth state
    } finally {
      setLoading(false);
      console.log('🔄 AuthContext: checkAuthStatus finished, loading set to false.'); // Added log
    }
  };

  const signIn = async (email, password) => {
    const logPrefix = '🔍 AuthContext.signIn';
    console.log(`${logPrefix}: Function called`, {
      email: email?.toLowerCase()?.trim(),
      hasPassword: !!password,
      passwordLength: password?.length,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      cookiesEnabled: navigator.cookieEnabled
    });
    
    try {
      console.log(`${logPrefix}: Calling authAPI.signIn...`);
      const response = await authAPI.signIn(email, password);
      console.log(`${logPrefix}: API response received`, {
        success: response.success,
        hasUser: !!response.user,
        userEmail: response.user?.email,
        userId: response.user?.user_id,
        message: response.message
      });
      
      if (response.success && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
        console.log(`${logPrefix}: ✅ Authentication successful!`, {
          user: response.user,
          isAuthenticated: true
        });
        
        // Wait a moment for the cookie to be set, then verify auth status
        setTimeout(async () => {
          try {
            await checkAuthStatus();
          } catch (err) {
            console.warn(`${logPrefix}: Auth status check after login failed:`, err);
          }
        }, 200);
        
        return { success: true, user: response.user };
      } else {
        console.warn(`${logPrefix}: ⚠️ Response indicates not authenticated`, {
          success: response.success,
          hasUser: !!response.user,
          response: response
        });
        return { success: false, error: 'Authentication failed' };
      }
    } catch (error) {
      // Comprehensive error logging
      const errorLog = {
        errorType: error.constructor.name,
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        cookiesEnabled: navigator.cookieEnabled
      };
      
      if (error.response) {
        // HTTP error response
        errorLog.response = {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: {
            'content-type': error.response.headers['content-type'],
            'content-length': error.response.headers['content-length'],
            'set-cookie': error.response.headers['set-cookie'] ? 'Present' : 'Missing'
          }
        };
        
        console.error(`${logPrefix}: ❌ HTTP Error Response`, errorLog);
      } else if (error.request) {
        // Request made but no response
        errorLog.request = {
          method: error.config?.method,
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          fullURL: `${error.config?.baseURL}${error.config?.url}`,
          withCredentials: error.config?.withCredentials,
          headers: error.config?.headers,
          timeout: error.config?.timeout
        };
        
        console.error(`${logPrefix}: ❌ Network Error (No Response)`, errorLog);
      } else {
        // Error in request setup
        errorLog.setupError = {
          message: error.message,
          config: error.config
        };
        
        console.error(`${logPrefix}: ❌ Request Setup Error`, errorLog);
      }
      
      // Also log to console as a single object for easier inspection
      console.error(`${logPrefix}: Full Error Object`, JSON.stringify(errorLog, null, 2));
      
      // Provide user-friendly error messages
      let errorMessage = 'Sign in failed';
      
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail || error.response.data?.message || error.response.data?.error;
        
        console.log(`${logPrefix}: Processing ${status} error`, { detail });
        
        if (status === 401) {
          errorMessage = detail || 'Invalid email or password. Please check your credentials and try again.';
        } else if (status === 403) {
          errorMessage = detail || 'Please verify your email address before signing in.';
        } else if (status === 404) {
          errorMessage = detail || 'No account found with this email address.';
        } else if (status === 500) {
          errorMessage = detail || 'Server error. Please try again later.';
        } else if (detail) {
          errorMessage = detail;
        } else {
          errorMessage = `Sign in failed (${status}). Please try again.`;
        }
      } else if (error.message) {
        if (error.message.includes('401')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.message.includes('Network Error') || error.message.includes('timeout')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      return {
        success: false,
        error: errorMessage,
        errorDetails: errorLog // Include for debugging
      };
    }
  };

  const signUp = async (userData) => { // Correctly accepts userData object
    console.log('🔍 AuthContext: SignUp function called with:', userData);
    try {
      const response = await authAPI.signUp(userData);
      console.log('✅ AuthContext: SignUp API response received:', response);
      return { success: true, message: response.message, requiresVerification: response.requiresVerification, userSub: response.userSub };
    } catch (error) {
      console.error('💥 AuthContext: Sign up error caught:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Sign up failed' 
      };
    }
  };

  const signOut = async () => {
    console.log('🔍 AuthContext: SignOut function called.');
    try {
      await authAPI.signOut();
      setUser(null);
      setIsAuthenticated(false);
      console.log('👋 AuthContext: SignOut successful.');
      return { success: true };
    } catch (error) {
      console.error('💥 AuthContext: Sign out error caught:', error);
      // Even if API call fails, clear local state to ensure logout
      setUser(null);
      setIsAuthenticated(false);
      return { success: true }; // Still return success if local state cleared
    }
  };

  const updateProfile = async (profileData) => {
    console.log('🔍 AuthContext: Update profile called with:', profileData);
    try {
      const response = await authAPI.updateProfile(profileData);
      if (response.success && response.profile) {
        setUser(response.profile); // Assuming API returns updated user data
      }
      return { success: true, message: response.message };
    } catch (error) {
      console.error('💥 AuthContext: Profile update error:', error);
      return { success: false, error: error.response?.data?.detail || 'Profile update failed' };
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};